// src/routes/Contracts.tsx
//
// RFA Phase 6f — the contract-years assignment view. Every offseason
// acquisition (RFA match/win, offseason waiver award, rookie picks when
// Phase 5 ships) lands as a provisional 1-year yearsPending contract; the
// owner picks the real length here until the commissioner-set cutdown
// instant locks it (scheduled auto-lock with a manual backup on /commish).
import { useMutation, useQuery } from 'convex/react';
import { useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { Countdown } from '../components/Countdown';

const SIGNED_FOR_LABELS: Record<string, string> = {
  rfa: 'RFA auction',
  waiver: 'Waiver',
  rookie: 'Rookie draft',
};

export default function Contracts() {
  const me = useQuery(api.franchises.myFranchise);
  // Don't subscribe until the franchise is bound — getMyPendingContracts
  // throws NO_FRANCHISE for signed-in users without one.
  const data = useQuery(api.contractYears.getMyPendingContracts, me ? {} : 'skip');
  const setYears = useMutation(api.contractYears.setContractYears);

  const [error, setError] = useState<string | null>(null);
  const [submittingId, setSubmittingId] = useState<Id<'contracts'> | null>(null);

  if (me === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }

  if (data === undefined) return <div className="p-6">Loading…</div>;

  const locked = data.yearsLockedAt !== null;

  const onSetYears = async (contractId: Id<'contracts'>, years: number) => {
    setError(null);
    setSubmittingId(contractId);
    try {
      await setYears({ contractId, years });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold">Contracts</h1>
      <p className="mt-1 text-sm text-gray-600">
        Assign contract lengths to your offseason acquisitions. Years are editable until they lock
        at the cutdown deadline.
      </p>

      {/* Cutdown / lock state */}
      <div className="mt-3 flex flex-wrap items-center gap-4 rounded border bg-white px-3 py-2 shadow-sm">
        {locked ? (
          <span className="text-sm">
            <strong className="text-amber-700">Contract years are locked</strong>{' '}
            <span className="text-gray-500">
              (since {new Date(data.yearsLockedAt as number).toLocaleString()})
            </span>
          </span>
        ) : data.cutdownAt !== null ? (
          <Countdown to={data.cutdownAt} label="Years lock in" expiredText="locked" />
        ) : (
          <span className="text-sm text-gray-600">
            No cutdown date set yet — the commissioner will schedule the lock.
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {locked ? (
        <p className="mt-4 text-sm text-gray-600">
          Contract years are final for this season — see your roster.
        </p>
      ) : data.contracts.length === 0 ? (
        <p className="mt-4 text-sm text-gray-600">No contracts are awaiting a years assignment.</p>
      ) : (
        <table className="mt-4 w-full border-collapse">
          <thead>
            <tr className="border-b text-left text-sm text-gray-500">
              <th className="py-2">Player</th>
              <th>Acquired via</th>
              <th>Contract years</th>
            </tr>
          </thead>
          <tbody>
            {data.contracts.map((c) => (
              <tr key={c.contractId} className="border-b">
                <td className="py-2">
                  <span className="font-medium">{c.playerName}</span>{' '}
                  <span className="text-sm text-gray-500">
                    ({c.position} {c.nflTeam})
                  </span>
                </td>
                <td className="text-sm">{SIGNED_FOR_LABELS[c.signedFor] ?? c.signedFor}</td>
                <td>
                  <select
                    className="border rounded px-2 py-1"
                    value={c.yearsRemaining}
                    disabled={submittingId === c.contractId}
                    onChange={(e) => onSetYears(c.contractId, Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5].map((y) => (
                      <option key={y} value={y}>
                        {y} {y === 1 ? 'year' : 'years'}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
